'use client';
import React, { useState, useEffect, useCallback } from 'react';
import Link from 'next/link';
import { useParams, useRouter } from 'next/navigation';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

interface JobListing {
  id: string;
  title: string;
  description: string | null;
  niche: string | null;
  budget: string | null;
  duration: string | null;
  requirements: string[] | null;
  campaign_start: string | null;
  campaign_end: string | null;
  status: string;
  views_count: number;
  created_at: string;
  business_id: string;
  pay_type: string | null;
  salary_min: number | null;
  salary_max: number | null;
  salary_currency: string | null;
  contract_type: string | null;
  location_type: string | null;
  business_profiles: {
    id: string;
    company_name: string;
    industry: string | null;
    description: string | null;
    website: string | null;
    logo_url: string | null;
    location: string | null;
  } | null;
}

interface PortfolioItem {
  id: string;
  title: string;
  brand: string | null;
  gmv: string | null;
  viewers: string | null;
  duration: string | null;
  description: string | null;
  thumbnail_url: string | null;
}

interface SimilarJob {
  id: string;
  title: string;
  budget: string | null;
  niche: string | null;
  business_profiles: { company_name: string; logo_url: string | null } | null;
}

export default function JobDetailPage() {
  const params = useParams();
  const router = useRouter();
  const { user } = useAuth();
  const supabase = createClient();

  const [job, setJob] = useState<JobListing | null>(null);
  const [similarJobs, setSimilarJobs] = useState<SimilarJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Apply modal state
  const [showApplyModal, setShowApplyModal] = useState(false);
  const [portfolioItems, setPortfolioItems] = useState<PortfolioItem[]>([]);
  const [selectedPortfolioIds, setSelectedPortfolioIds] = useState<string[]>([]);
  const [coverMessage, setCoverMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [alreadyApplied, setAlreadyApplied] = useState(false);

  const jobId = params?.id as string;

  const fetchJob = useCallback(async () => {
    if (!jobId) return;
    setLoading(true);
    setError(null);
    try {
      const { data, error: fetchError } = await supabase
        .from('job_listings')
        .select(`
          *,
          business_profiles (
            id, company_name, industry, description, website, logo_url, location
          )
        `)
        .eq('id', jobId)
        .single();

      if (fetchError) throw fetchError;
      setJob(data as JobListing);

      // Increment views
      await supabase
        .from('job_listings')
        .update({ views_count: (data.views_count || 0) + 1 })
        .eq('id', jobId);

      // Fetch similar jobs (same niche, exclude current)
      if (data?.niche) {
        const { data: similar } = await supabase
          .from('job_listings')
          .select('id, title, budget, niche, business_profiles(company_name, logo_url)')
          .eq('niche', data.niche)
          .eq('status', 'active')
          .neq('id', jobId)
          .limit(3);
        setSimilarJobs((similar as SimilarJob[]) || []);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load job details.');
    } finally {
      setLoading(false);
    }
  }, [jobId]);

  const checkAlreadyApplied = useCallback(async () => {
    if (!user || !jobId) return;
    const { data: sellerProfile } = await supabase
      .from('seller_profiles')
      .select('id')
      .eq('user_id', user.id)
      .single();
    if (!sellerProfile) return;

    const { data: existing } = await supabase
      .from('job_applications')
      .select('id')
      .eq('seller_id', sellerProfile.id)
      .eq('job_listing_id', jobId)
      .maybeSingle();
    if (existing) setAlreadyApplied(true);
  }, [user, jobId]);

  const fetchPortfolioItems = useCallback(async () => {
    if (!user) return;
    const { data: sellerProfile } = await supabase
      .from('seller_profiles')
      .select('id')
      .eq('user_id', user.id)
      .single();
    if (!sellerProfile) return;

    const { data: items } = await supabase
      .from('portfolio_items')
      .select('id, title, brand, gmv, viewers, duration, description, thumbnail_url')
      .eq('seller_id', sellerProfile.id);
    setPortfolioItems((items as PortfolioItem[]) || []);
  }, [user]);

  useEffect(() => {
    fetchJob();
  }, [fetchJob]);

  useEffect(() => {
    if (user) {
      checkAlreadyApplied();
    }
  }, [user, checkAlreadyApplied]);

  const handleOpenApply = async () => {
    if (!user) {
      router.push('/login');
      return;
    }
    await fetchPortfolioItems();
    setShowApplyModal(true);
  };

  const togglePortfolioItem = (id: string) => {
    setSelectedPortfolioIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleSubmitApplication = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !job) return;
    setSubmitting(true);
    setSubmitError(null);

    try {
      const { data: sellerProfile, error: spError } = await supabase
        .from('seller_profiles')
        .select('id')
        .eq('user_id', user.id)
        .single();

      if (spError || !sellerProfile) {
        throw new Error('Seller profile not found. Please complete your profile first.');
      }

      const { error: insertError } = await supabase.from('job_applications').insert({
        seller_id: sellerProfile.id,
        job_listing_id: job.id,
        job_title: job.title,
        business_name: job.business_profiles?.company_name || '',
        business_id: job.business_id,
        cover_message: coverMessage,
        portfolio_item_ids: selectedPortfolioIds,
        status: 'pending',
      });

      if (insertError) throw insertError;

      setSubmitted(true);
      setAlreadyApplied(true);
    } catch (err: any) {
      setSubmitError(err.message || 'Failed to submit application.');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffH = Math.floor(diffMs / 3600000);
    const diffD = Math.floor(diffMs / 86400000);
    if (diffH < 1) return 'Just now';
    if (diffH < 24) return `${diffH}h ago`;
    if (diffD < 7) return `${diffD}d ago`;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const CONTRACT_TYPE_LABELS: Record<string, string> = {
    per_job_booking: 'Per Job / Booking',
    permanent: 'Permanent',
    temporary: 'Temporary',
    freelance: 'Freelance',
    part_time: 'Part-Time',
    full_time: 'Full-Time',
    contract: 'Fixed-Term Contract',
  };

  const PAY_TYPE_LABELS: Record<string, string> = {
    hourly: 'Hourly Rate',
    per_stream: 'Per Stream',
    per_job: 'Per Job / Flat Fee',
    monthly: 'Monthly Salary',
    yearly: 'Annual Salary',
    negotiable: 'Negotiable',
  };

  const formatPay = (job: JobListing): string => {
    if (job.budget) return job.budget;
    if (job.pay_type === 'negotiable') return 'Negotiable';
    if (job.salary_min) {
      const currency = job.salary_currency || 'USD';
      const symbol = currency === 'USD' ? '$' : currency === 'GBP' ? '£' : currency === 'EUR' ? '€' : '$';
      const suffix: Record<string, string> = { hourly: '/hr', per_stream: '/stream', per_job: '/job', monthly: '/mo', yearly: '/yr' };
      const suf = suffix[job.pay_type || ''] || '';
      const min = job.salary_min.toLocaleString();
      const max = job.salary_max ? job.salary_max.toLocaleString() : null;
      return max ? `${symbol}${min}–${symbol}${max}${suf}` : `${symbol}${min}${suf}`;
    }
    return 'See description';
  };

  if (loading) {
    return (
      <main className="font-montserrat bg-white text-dark overflow-x-hidden">
        <Navbar />
        <section className="pt-28 pb-14 bg-neutral-01 min-h-screen">
          <div className="max-w-[1200px] mx-auto px-6">
            <div className="animate-pulse">
              <div className="h-4 bg-neutral-02 rounded w-48 mb-8" />
              <div className="flex flex-col lg:flex-row gap-10">
                <div className="flex-1 space-y-4">
                  <div className="h-10 bg-neutral-02 rounded w-3/4" />
                  <div className="h-4 bg-neutral-02 rounded w-1/2" />
                  <div className="h-48 bg-neutral-02 rounded-[24px]" />
                  <div className="h-48 bg-neutral-02 rounded-[24px]" />
                </div>
                <div className="lg:w-[400px]">
                  <div className="h-96 bg-neutral-02 rounded-[28px]" />
                </div>
              </div>
            </div>
          </div>
        </section>
        <Footer />
      </main>
    );
  }

  if (error || !job) {
    return (
      <main className="font-montserrat bg-white text-dark overflow-x-hidden">
        <Navbar />
        <section className="pt-28 pb-14 bg-neutral-01 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <p className="text-[18px] font-bold text-dark mb-2">Job not found</p>
            <p className="text-[14px] text-dark/50 mb-6">{error || 'This job listing may have been removed.'}</p>
            <Link href="/jobs" className="font-bold text-[14px] text-white bg-dark px-6 py-3 rounded-pill hover:bg-opacity-80 transition-all">
              Browse Jobs
            </Link>
          </div>
        </section>
        <Footer />
      </main>
    );
  }

  const business = job.business_profiles;
  const accentColors = ['bg-theme-01', 'bg-theme-03', 'bg-theme-04', 'bg-theme-02'];

  return (
    <main className="font-montserrat bg-white text-dark overflow-x-hidden">
      <Navbar />

      {/* Hero */}
      <section className="pt-28 pb-14 bg-neutral-01">
        <div className="max-w-[1200px] mx-auto px-6">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 text-[13px] text-dark/40 mb-8">
            <Link href="/" className="hover:text-dark transition-colors">Home</Link>
            <span>/</span>
            <Link href="/jobs" className="hover:text-dark transition-colors">Job Board</Link>
            <span>/</span>
            <span className="text-dark truncate max-w-[200px]">{job.title}</span>
          </div>

          <div className="flex flex-col lg:flex-row gap-10">
            {/* Left: Job Info */}
            <div className="flex-1">
              <div className="flex items-start gap-5 mb-6">
                <div className="w-16 h-16 bg-theme-01 rounded-[20px] flex items-center justify-center overflow-hidden flex-shrink-0 shadow-md">
                  {business?.logo_url ? (
                    <img src={business.logo_url} alt={`${business.company_name} company logo`} className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-[22px] font-bold text-white">
                      {business?.company_name?.charAt(0) || '?'}
                    </span>
                  )}
                </div>
                <div>
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    {job.status === 'active' && (
                      <span className="text-[11px] font-bold text-white bg-dark px-3 py-1 rounded-full">Active</span>
                    )}
                    <span className="text-[11px] font-medium text-dark/50 bg-white px-3 py-1 rounded-full border border-neutral-02">
                      Posted {formatDate(job.created_at)}
                    </span>
                    {job.views_count > 0 && (
                      <span className="text-[11px] font-medium text-dark/50 bg-white px-3 py-1 rounded-full border border-neutral-02">
                        {job.views_count} views
                      </span>
                    )}
                  </div>
                  <h1 className="text-[30px] md:text-[38px] font-bold text-dark leading-tight">{job.title}</h1>
                  {business && (
                    <Link href={`/business/${business.id}`} className="text-[15px] text-dark/60 hover:text-dark transition-colors font-medium mt-1 inline-block">
                      {business.company_name} →
                    </Link>
                  )}
                </div>
              </div>

              {/* Quick Stats */}
              <div className="flex flex-wrap gap-3 mb-8">
                {(job.budget || job.salary_min || job.pay_type === 'negotiable') && (
                  <div className="flex items-center gap-2 bg-white px-4 py-2.5 rounded-full border border-neutral-02">
                    <span className="text-[14px]">💰</span>
                    <span className="text-[13px] font-semibold text-dark">{formatPay(job)}</span>
                  </div>
                )}
                {job.pay_type && job.pay_type !== 'per_stream' && (
                  <div className="flex items-center gap-2 bg-white px-4 py-2.5 rounded-full border border-neutral-02">
                    <span className="text-[14px]">📊</span>
                    <span className="text-[13px] font-semibold text-dark">{PAY_TYPE_LABELS[job.pay_type] || job.pay_type}</span>
                  </div>
                )}
                {job.contract_type && (
                  <div className="flex items-center gap-2 bg-white px-4 py-2.5 rounded-full border border-neutral-02">
                    <span className="text-[14px]">📋</span>
                    <span className="text-[13px] font-semibold text-dark">{CONTRACT_TYPE_LABELS[job.contract_type] || job.contract_type}</span>
                  </div>
                )}
                {job.duration && (
                  <div className="flex items-center gap-2 bg-white px-4 py-2.5 rounded-full border border-neutral-02">
                    <span className="text-[14px]">⏱️</span>
                    <span className="text-[13px] font-semibold text-dark">{job.duration}</span>
                  </div>
                )}
                {job.location_type ? (
                  <div className="flex items-center gap-2 bg-white px-4 py-2.5 rounded-full border border-neutral-02">
                    <span className="text-[14px]">{job.location_type === 'remote' ? '🌐' : job.location_type === 'on_site' ? '📍' : '🔀'}</span>
                    <span className="text-[13px] font-semibold text-dark">
                      {job.location_type === 'remote' ? 'Remote' : job.location_type === 'on_site' ? 'On-Site' : 'Hybrid'}
                    </span>
                  </div>
                ) : business?.location ? (
                  <div className="flex items-center gap-2 bg-white px-4 py-2.5 rounded-full border border-neutral-02">
                    <span className="text-[14px]">📍</span>
                    <span className="text-[13px] font-semibold text-dark">{business.location}</span>
                  </div>
                ) : null}
                {job.niche && (
                  <div className="flex items-center gap-2 bg-white px-4 py-2.5 rounded-full border border-neutral-02">
                    <span className="text-[14px]">🎯</span>
                    <span className="text-[13px] font-semibold text-dark">{job.niche}</span>
                  </div>
                )}
              </div>

              {/* Job Description */}
              {job.description && (
                <div className="bg-white rounded-[24px] p-7 mb-6 border border-neutral-02">
                  <h2 className="text-[20px] font-bold text-dark mb-4">Job Brief</h2>
                  <p className="text-[15px] text-dark/70 leading-relaxed whitespace-pre-line">{job.description}</p>
                </div>
              )}

              {/* Requirements */}
              {job.requirements && job.requirements.length > 0 && (
                <div className="bg-white rounded-[24px] p-7 mb-6 border border-neutral-02">
                  <h2 className="text-[20px] font-bold text-dark mb-4">Requirements</h2>
                  <ul className="flex flex-col gap-2">
                    {job.requirements.map((req, i) => (
                      <li key={i} className="flex items-start gap-2.5 text-[14px] text-dark/70">
                        <div className="w-5 h-5 bg-theme-green/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                          <svg width="10" height="10" viewBox="0 0 24 24" fill="none">
                            <path d="M20 6L9 17l-5-5" stroke="#2BD886" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </div>
                        {req}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Campaign Dates */}
              {(job.campaign_start || job.campaign_end) && (
                <div className="bg-white rounded-[24px] p-7 border border-neutral-02">
                  <h2 className="text-[20px] font-bold text-dark mb-5">Campaign Timeline</h2>
                  <div className="flex flex-col gap-3">
                    {job.campaign_start && (
                      <div className="flex items-center justify-between py-3 border-b border-neutral-02">
                        <p className="text-[14px] font-bold text-dark">Start Date</p>
                        <span className="text-[14px] text-dark/70">{new Date(job.campaign_start).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</span>
                      </div>
                    )}
                    {job.campaign_end && (
                      <div className="flex items-center justify-between py-3">
                        <p className="text-[14px] font-bold text-dark">End Date</p>
                        <span className="text-[14px] text-dark/70">{new Date(job.campaign_end).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Right: Apply Sidebar */}
            <div className="lg:w-[400px] flex-shrink-0">
              <div className="sticky top-24 flex flex-col gap-4">
                {/* Apply Card */}
                <div className="bg-neutral-01 rounded-[28px] p-7">
                  <h2 className="text-[22px] font-bold text-dark mb-1">
                    {alreadyApplied ? 'Application Sent!' : 'Apply for this role'}
                  </h2>
                  <p className="text-[13px] text-dark/50 mb-6">
                    {alreadyApplied
                      ? `${business?.company_name || 'The brand'} has been notified. They typically respond within 24–48 hours.`
                      : `${business?.company_name || 'The brand'} will be notified instantly.`}
                  </p>

                  {alreadyApplied ? (
                    <div className="text-center">
                      <div className="w-16 h-16 bg-theme-green/20 rounded-full flex items-center justify-center mx-auto mb-5">
                        <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                          <path d="M20 6L9 17l-5-5" stroke="#2BD886" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </div>
                      <Link
                        href="/jobs"
                        className="font-bold text-[14px] text-dark border border-dark/20 px-6 py-3 rounded-pill hover:bg-neutral-02 transition-all duration-200 inline-block"
                      >
                        Browse More Jobs
                      </Link>
                    </div>
                  ) : (
                    <button
                      onClick={handleOpenApply}
                      className="w-full font-bold text-[15px] text-white bg-dark py-4 rounded-pill hover:bg-opacity-80 transition-all duration-200"
                    >
                      Apply Now
                    </button>
                  )}

                  {!user && !alreadyApplied && (
                    <p className="text-[11px] text-dark/40 text-center mt-3">
                      You need to <Link href="/login" className="underline">sign in</Link> to apply.
                    </p>
                  )}
                </div>

                {/* Brand Quick Info */}
                {business && (
                  <div className="bg-white rounded-[24px] p-5 border border-neutral-02">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 rounded-xl overflow-hidden bg-theme-01 flex items-center justify-center flex-shrink-0">
                        {business.logo_url ? (
                          <img src={business.logo_url} alt={`${business.company_name} brand logo`} className="w-full h-full object-cover" />
                        ) : (
                          <span className="text-[14px] font-bold text-white">{business.company_name.charAt(0)}</span>
                        )}
                      </div>
                      <div>
                        <p className="font-bold text-dark text-[14px]">{business.company_name}</p>
                        {business.industry && (
                          <p className="text-[12px] text-dark/50">{business.industry}</p>
                        )}
                      </div>
                    </div>
                    {business.description && (
                      <p className="text-[13px] text-dark/60 leading-relaxed mb-4 line-clamp-3">{business.description}</p>
                    )}
                    <Link
                      href={`/business/${business.id}`}
                      className="w-full font-bold text-[13px] text-dark border border-dark/20 py-2.5 rounded-pill hover:bg-neutral-01 transition-all duration-200 flex items-center justify-center gap-1.5"
                    >
                      View Brand Profile
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none">
                        <path d="M5 12h14M12 5l7 7-7 7" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </Link>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Similar Jobs */}
      {similarJobs.length > 0 && (
        <section className="py-16 bg-neutral-01">
          <div className="max-w-[1200px] mx-auto px-6">
            <h2 className="text-[28px] font-bold text-dark mb-8">Similar Jobs</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {similarJobs.map((sj, i) => (
                <Link
                  key={sj.id}
                  href={`/jobs/${sj.id}`}
                  className="bg-white rounded-[24px] p-6 hover:-translate-y-1 hover:shadow-lg transition-all duration-300 group block"
                >
                  <div className={`w-10 h-10 ${accentColors[i % accentColors.length]} rounded-2xl flex items-center justify-center mb-4 overflow-hidden`}>
                    {sj.business_profiles?.logo_url ? (
                      <img src={sj.business_profiles.logo_url} alt={`${sj.business_profiles.company_name} logo`} className="w-full h-full object-cover" />
                    ) : (
                      <span className="text-[14px] font-bold text-white">{sj.business_profiles?.company_name?.charAt(0) || '?'}</span>
                    )}
                  </div>
                  <h3 className="font-bold text-dark text-[15px] mb-1 leading-snug">{sj.title}</h3>
                  <p className="text-[13px] text-dark/50 mb-3">{sj.business_profiles?.company_name}</p>
                  <div className="flex items-center justify-between pt-3 border-t border-neutral-02">
                    <span className="text-[13px] font-bold text-dark">{sj.budget || 'Negotiable'}</span>
                    <span className="font-bold text-[12px] text-dark group-hover:text-theme-01 transition-colors flex items-center gap-1">
                      View
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none">
                        <path d="M5 12h14M12 5l7 7-7 7" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <Footer />

      {/* Apply Modal */}
      {showApplyModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-dark/60 backdrop-blur-sm" onClick={() => !submitting && setShowApplyModal(false)} />
          <div className="relative bg-white rounded-[28px] w-full max-w-[560px] max-h-[90vh] overflow-y-auto shadow-2xl">
            <div className="p-7">
              {/* Modal Header */}
              <div className="flex items-start justify-between mb-6">
                <div>
                  <h2 className="text-[22px] font-bold text-dark">Submit Proposal</h2>
                  <p className="text-[13px] text-dark/50 mt-0.5">Applying to: <span className="font-semibold text-dark">{job.title}</span></p>
                </div>
                <button
                  onClick={() => !submitting && setShowApplyModal(false)}
                  className="w-8 h-8 rounded-full bg-neutral-01 flex items-center justify-center hover:bg-neutral-02 transition-colors flex-shrink-0 ml-4"
                  aria-label="Close modal"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                    <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
                  </svg>
                </button>
              </div>

              {submitted ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-theme-green/20 rounded-full flex items-center justify-center mx-auto mb-5">
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                      <path d="M20 6L9 17l-5-5" stroke="#2BD886" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <h3 className="text-[22px] font-bold text-dark mb-2">Application Sent!</h3>
                  <p className="text-[14px] text-dark/60 leading-relaxed mb-6">
                    {business?.company_name || 'The brand'} has been notified. They typically respond within 24–48 hours.
                  </p>
                  <button
                    onClick={() => setShowApplyModal(false)}
                    className="font-bold text-[14px] text-dark border border-dark/20 px-6 py-3 rounded-pill hover:bg-neutral-01 transition-all duration-200"
                  >
                    Close
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmitApplication} className="flex flex-col gap-5">
                  {/* Portfolio Selection */}
                  <div>
                    <label className="text-[12px] font-bold text-dark/60 mb-2 block uppercase tracking-wide">
                      Select Portfolio Items <span className="text-dark/30 normal-case font-normal">(optional)</span>
                    </label>
                    {portfolioItems.length === 0 ? (
                      <div className="bg-neutral-01 rounded-xl p-4 text-center">
                        <p className="text-[13px] text-dark/50">No portfolio items found.</p>
                        <Link href="/dashboard/seller" className="text-[12px] text-dark underline mt-1 inline-block">
                          Add portfolio items in your dashboard
                        </Link>
                      </div>
                    ) : (
                      <div className="flex flex-col gap-2 max-h-[200px] overflow-y-auto pr-1">
                        {portfolioItems.map((item) => {
                          const selected = selectedPortfolioIds.includes(item.id);
                          return (
                            <button
                              key={item.id}
                              type="button"
                              onClick={() => togglePortfolioItem(item.id)}
                              className={`flex items-center gap-3 p-3 rounded-xl border-2 text-left transition-all ${
                                selected
                                  ? 'border-dark bg-dark/5' :'border-neutral-02 bg-neutral-01 hover:border-dark/30'
                              }`}
                            >
                              <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 ${
                                selected ? 'border-dark bg-dark' : 'border-dark/30'
                              }`}>
                                {selected && (
                                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none">
                                    <path d="M20 6L9 17l-5-5" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                                  </svg>
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-[13px] font-bold text-dark truncate">{item.title}</p>
                                <p className="text-[11px] text-dark/50">
                                  {item.brand && `${item.brand} · `}{item.gmv && `GMV: ${item.gmv}`}
                                </p>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* Cover Message */}
                  <div>
                    <label className="text-[12px] font-bold text-dark/60 mb-1.5 block uppercase tracking-wide">
                      Why are you a great fit? <span className="text-red-400">*</span>
                    </label>
                    <textarea
                      rows={5}
                      required
                      placeholder={`Tell ${business?.company_name || 'the brand'} why you're the right seller for this role...`}
                      value={coverMessage}
                      onChange={(e) => setCoverMessage(e.target.value)}
                      className="w-full bg-neutral-01 rounded-xl px-4 py-3 text-[14px] font-medium text-dark outline-none focus:ring-2 focus:ring-dark/20 transition-all border border-neutral-02 resize-none"
                    />
                  </div>

                  {submitError && (
                    <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3">
                      <p className="text-[13px] text-red-600">{submitError}</p>
                    </div>
                  )}

                  <div className="flex gap-3 pt-1">
                    <button
                      type="button"
                      onClick={() => setShowApplyModal(false)}
                      disabled={submitting}
                      className="flex-1 font-bold text-[14px] text-dark border border-dark/20 py-3.5 rounded-pill hover:bg-neutral-01 transition-all duration-200 disabled:opacity-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={submitting}
                      className="flex-1 font-bold text-[15px] text-white bg-dark py-3.5 rounded-pill hover:bg-opacity-80 transition-all duration-200 disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      {submitting ? (
                        <>
                          <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                          </svg>
                          Submitting...
                        </>
                      ) : 'Submit Proposal'}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </main>
  );
}
